/**
 * tabsStore — Sistema de abas para múltiplos projetos abertos
 *
 * Cada aba possui seu próprio snapshot (tabelas, rels, grupos, nome, dialeto),
 * histórico de undo/redo e estado de viewport.
 *
 * O diagramStore, historyStore e viewportStore continuam sendo a "view ativa" —
 * eles refletem sempre o projeto da aba corrente. Ao trocar de aba, o estado
 * da aba anterior é salvo aqui e o da nova aba é carregado nos stores ativos.
 *
 * Persistência: localStorage, chave morpherd_workspace_v1 (mantida para
 * compatibilidade com dados existentes — renomear em fase futura).
 */

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type {
  ProjectTab,
  ProjectTabSnapshot,
  ProjectTabHistory,
  DatabaseDialect,
  ProjectViewState,
  ProjectTabVersionsState,
} from '@/types'
import { genId } from '@/utils/id'
import { SCALE_DEFAULT } from './viewportStore'
import { INIT_DEFAULTS } from '@/utils/normalizers'

// Lazy imports — resolvidas em runtime para evitar ciclos de módulo.
import { useDiagramStore } from './diagramStore'
import { useHistoryStore } from './historyStore'
import { useViewportStore } from './viewportStore'
import { useTabViewStore } from './tabViewStore'
import { useWorkspaceStore } from './workspaceStore'

export const MAX_OPEN_TABS = 10

// ── Tipos internos ────────────────────────────────────────────────────────────

interface TabsState {
  activeTabId: string | null
  tabs:        ProjectTab[]
  /** Previne que o auto-sync dispare durante uma troca de aba */
  isSwitching: boolean

  // ── Ações ────────────────────────────────────────────────────────────────
  createTab:          (options?: CreateTabOptions) => string
  createEmptyTab:     (options?: { title?: string; dialect?: DatabaseDialect }) => string
  createTabFromActiveStores: () => string
  activateTab:        (tabId: string) => void
  closeTab:           (tabId: string) => void
  syncFromActiveStores: () => void
  markDirty:          (tabId: string) => void
  markClean:          (tabId: string) => void
  updateTabTitle:     (tabId: string, title: string) => void
  updateTab:          (tabId: string, changes: { title?: string; remoteName?: string | null; dialect?: DatabaseDialect }) => void
  initWorkspace:      () => void
}

interface CreateTabOptions {
  snapshot?:  ProjectTabSnapshot
  history?:   ProjectTabHistory
  title?:     string
  remoteName?: string | null
  projectId?: string
  workspaceId?: number | null
  viewState?: ProjectViewState
  versions?:  ProjectTabVersionsState
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function deepClone<T>(value: T): T {
  if (typeof structuredClone !== 'undefined') {
    return structuredClone(value)
  }
  return JSON.parse(JSON.stringify(value)) as T
}

function snapshotFromDiagram(): ProjectTabSnapshot {
  const s = useDiagramStore.getState()
  return deepClone({
    tables:      s.tables,
    rels:        s.rels,
    groups:      s.groups,
    defaults:    s.defaults,
    projectName: s.projectName,
    dialect:     s.dialect,
  })
}

function historyFromStore(): ProjectTabHistory {
  const h = useHistoryStore.getState()
  return deepClone({
    undoStack: h.undoStack as ProjectTabHistory['undoStack'],
    redoStack: h.redoStack as ProjectTabHistory['redoStack'],
  })
}

function viewportFromStore() {
  const v = useViewportStore.getState()
  return deepClone({ sc: v.sc, tx: v.tx, ty: v.ty })
}

function viewStateFromStore(): ProjectViewState {
  const v = useTabViewStore.getState()
  return deepClone({
    hiddenTableIds: v.hiddenTableIds,
  })
}

function versionsFromStore(): ProjectTabVersionsState {
  return { currentVersionId: null, projectVersions: [], changeLog: [] }
}

function scheduleResetView(tables: ProjectTabSnapshot['tables']) {
  requestAnimationFrame(() => {
    useViewportStore.getState().resetView(tables)
  })
}

function normalizeViewState(viewState?: Partial<ProjectViewState> | null): ProjectViewState {
  return {
    hiddenTableIds: Array.isArray(viewState?.hiddenTableIds)
      ? [...new Set(viewState.hiddenTableIds)]
      : [],
  }
}

function normalizeVersionsState(versions?: Partial<ProjectTabVersionsState> | null): ProjectTabVersionsState {
  return {
    currentVersionId: versions?.currentVersionId ?? null,
    projectVersions: Array.isArray(versions?.projectVersions) ? deepClone(versions.projectVersions) : [],
    changeLog: Array.isArray(versions?.changeLog) ? deepClone(versions.changeLog) : [],
  }
}

function buildTab(
  snapshot: ProjectTabSnapshot,
  history: ProjectTabHistory,
  viewport: { sc: number; tx: number; ty: number },
  options: CreateTabOptions,
): ProjectTab {
  const tabId = genId('tab')
  return {
    id:        tabId,
    projectId: options.projectId ?? tabId,
    workspaceId: options.workspaceId ?? null,
    remoteName: options.remoteName ?? null,
    title:     options.title ?? (snapshot.projectName || 'MorphERD'),
    dialect:   snapshot.dialect as DatabaseDialect,
    snapshot:  deepClone(snapshot),
    history:   deepClone(history),
    viewport:  deepClone(viewport),
    viewState: normalizeViewState(options.viewState),
    versions:  normalizeVersionsState(options.versions),
    isDirty:   false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }
}

// ── Store ─────────────────────────────────────────────────────────────────────

export const useTabsStore = create<TabsState>()(
  persist(
    (set, get) => ({
      activeTabId: null,
      tabs:        [],
      isSwitching: false,

      // ── createTab ─────────────────────────────────────────────────────────
      createTab: (options = {}) => {
        if (get().tabs.length >= MAX_OPEN_TABS) return ''
        const snapshot = options.snapshot ?? snapshotFromDiagram()
        const history  = options.history  ?? (options.snapshot
          ? { undoStack: [{ label: 'init', snapshot }], redoStack: [] }
          : historyFromStore()
        )
        const viewport = options.snapshot
          ? { sc: SCALE_DEFAULT, tx: 0, ty: 0 }
          : viewportFromStore()

        const tab = buildTab(snapshot, history, viewport, options)
        set(s => ({ tabs: [...s.tabs, tab] }))
        return tab.id
      },

      createEmptyTab: (options = {}) => get().createTab({
        snapshot: {
          tables: [],
          rels: [],
          groups: {},
          defaults: INIT_DEFAULTS,
          projectName: options.title?.trim() || 'MorphERD',
          dialect: options.dialect ?? 'postgresql',
        },
        history: {
          undoStack: [{
            label: 'init',
            snapshot: {
              tables: [],
              rels: [],
              groups: {},
              defaults: INIT_DEFAULTS,
              projectName: options.title?.trim() || 'MorphERD',
              dialect: options.dialect ?? 'postgresql',
              hiddenTableIds: [],
            },
          }],
          redoStack: [],
        },
        title: options.title?.trim() || 'MorphERD',
        viewState: { hiddenTableIds: [] },
        versions: {
          currentVersionId: null,
          projectVersions: [],
          changeLog: [],
        },
      }),

      // ── createTabFromActiveStores ─────────────────────────────────────────
      createTabFromActiveStores: () => {
        if (get().tabs.length >= MAX_OPEN_TABS) return ''
        const snapshot = snapshotFromDiagram()
        const history  = historyFromStore()
        const viewport = viewportFromStore()
        const tab = buildTab(snapshot, history, viewport, {
          viewState: viewStateFromStore(),
          versions: versionsFromStore(),
        })
        set(s => ({ tabs: [...s.tabs, tab] }))
        return tab.id
      },

      // ── activateTab ───────────────────────────────────────────────────────
      activateTab: (tabId: string) => {
        const { tabs, activeTabId, isSwitching } = get()
        if (isSwitching) return

        const newTab = tabs.find(t => t.id === tabId)
        if (!newTab) return

        if (activeTabId && activeTabId !== tabId) {
          const snapshot = snapshotFromDiagram()
          const history  = historyFromStore()
          const viewport = viewportFromStore()
          const viewState = viewStateFromStore()
          const versions = versionsFromStore()

          set(s => ({
            isSwitching: true,
            tabs: s.tabs.map(t =>
              t.id === activeTabId
                ? {
                    ...t,
                    title:     t.remoteName || snapshot.projectName || t.title,
                    dialect:   snapshot.dialect,
                    snapshot,
                    history,
                    viewport,
                    viewState,
                    versions,
                    updatedAt: new Date().toISOString(),
                  }
                : t,
            ),
          }))
        } else {
          set({ isSwitching: true })
        }

        set({ activeTabId: tabId })

        if (newTab.workspaceId != null) {
          void useWorkspaceStore.getState().selectWorkspaceById(newTab.workspaceId).catch(() => {
            /* best-effort */
          })
        }

        useDiagramStore.getState().loadDiagram(newTab.snapshot)

        useHistoryStore.setState({
          undoStack:    newTab.history.undoStack ?? [],
          redoStack:    newTab.history.redoStack ?? [],
          isRestoring:  false,
        })

        useViewportStore.setState({
          sc: newTab.viewport.sc,
          tx: newTab.viewport.tx,
          ty: newTab.viewport.ty,
        })

        useTabViewStore.setState(normalizeViewState(newTab.viewState))

        set({ isSwitching: false })
      },

      // ── closeTab ──────────────────────────────────────────────────────────
      closeTab: (tabId: string) => {
        const { tabs, activeTabId } = get()
        const tabIdx = tabs.findIndex(t => t.id === tabId)
        if (tabIdx < 0) return

        const newTabs = tabs.filter(t => t.id !== tabId)

        if (activeTabId === tabId) {
          set({ tabs: newTabs, activeTabId: null })

          if (newTabs.length > 0) {
            const nextIdx = Math.min(tabIdx, newTabs.length - 1)
            get().activateTab(newTabs[nextIdx].id)
          }
        } else {
          set({ tabs: newTabs })
        }
      },

      // ── syncFromActiveStores ──────────────────────────────────────────────
      syncFromActiveStores: () => {
        const { activeTabId, isSwitching } = get()
        if (!activeTabId || isSwitching) return

        const snapshot = snapshotFromDiagram()
        const history  = historyFromStore()
        const viewport = viewportFromStore()
        const viewState = viewStateFromStore()
        const versions = versionsFromStore()

        set(s => ({
          tabs: s.tabs.map(t =>
            t.id === activeTabId
              ? {
                  ...t,
                  title:     t.remoteName || snapshot.projectName || t.title,
                  dialect:   snapshot.dialect,
                  snapshot,
                  history,
                  viewport,
                  viewState,
                  versions,
                  isDirty:   true,
                  updatedAt: new Date().toISOString(),
                }
              : t,
          ),
        }))
      },

      // ── markDirty / markClean ─────────────────────────────────────────────
      markDirty: (tabId) =>
        set(s => ({ tabs: s.tabs.map(t => t.id === tabId ? { ...t, isDirty: true  } : t) })),

      markClean: (tabId) =>
        set(s => ({ tabs: s.tabs.map(t => t.id === tabId ? { ...t, isDirty: false } : t) })),

      // ── updateTabTitle ────────────────────────────────────────────────────
      updateTabTitle: (tabId, title) =>
        set(s => ({ tabs: s.tabs.map(t => t.id === tabId ? { ...t, title } : t) })),

      // ── updateTab ─────────────────────────────────────────────────────────
      updateTab: (tabId, changes) => {
        set(s => ({
          tabs: s.tabs.map(t => {
            if (t.id !== tabId) return t
            const newTitle   = changes.title   !== undefined ? changes.title   : t.title
            const newDialect = changes.dialect !== undefined ? changes.dialect : t.dialect
            const newRemoteName = 'remoteName' in changes ? changes.remoteName : t.remoteName
            return {
              ...t,
              remoteName: newRemoteName,
              title:     newTitle,
              dialect:   newDialect,
              snapshot:  { ...t.snapshot, projectName: newTitle, dialect: newDialect },
              isDirty:   true,
              updatedAt: new Date().toISOString(),
            }
          }),
        }))
        const { activeTabId } = get()
        if (tabId === activeTabId) {
          if (changes.title   !== undefined) useDiagramStore.getState().setProjectName(changes.title)
          if (changes.dialect !== undefined) useDiagramStore.getState().setDialect(changes.dialect)
        }
      },

      // ── initWorkspace ─────────────────────────────────────────────────────
      initWorkspace: () => {
        const { tabs, activeTabId } = get()

        if (tabs.length === 0) {
          set({ activeTabId: null })
        } else {
          const target = tabs.find(t => t.id === activeTabId) ?? tabs[0]
          if (target) {
            get().activateTab(target.id)
            scheduleResetView(target.snapshot.tables)
          }
        }
      },
    }),
    {
      name:    'morpherd_workspace_v1',
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        activeTabId: s.activeTabId,
        tabs: s.tabs.map(tab => ({
          ...tab,
          history: {
            undoStack: tab.history.undoStack.slice(-1),
            redoStack: [],
          },
        })),
      }),
    },
  ),
)
